import React, { Component } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import Immutable from "immutable";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classnames from "classnames";
import { Client } from "resourceModels";
import { updateOne } from "actions/resources";
import { getResource } from "selectors/resources";
import SvgIcon from "components/Common/SvgIcon";
import RadioButtons from "components/Common/RadioButtons";
import ColorInput from "components/Common/ColorInput";
import MediaUploader from "components/Common/MediaUploader";
import "./style.scss";

const options = [
  {
    value: "default",
    title: "Default Icon"
  },
  {
    value: "custom",
    title: "Custom"
  }
];

const buttonSizeOptions = [
  {
    value: 44,
    title: "Recommended",
    description: "44 x 44 Pixels"
  },
  {
    value: 56,
    title: "Large",
    description: "56 x 56 Pixels"
  },
  {
    value: 72,
    title: "Extra Large",
    description: "72 x 72 Pixels"
  }
];

const DEFAULT_CHAT_BUTTON_ICON = "https://d124s1zbdqkqqe.cloudfront.net/Dialogue.svg";

class ChatButton extends Component {
  /**
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.handleUpdateChatButtonIcon = this.handleUpdateChatButtonIcon.bind(this);
    this.handleUpdateChatButtonIconImage = this.handleUpdateChatButtonIconImage.bind(this);
    this.handleUpdateChatButtonColor = this.handleUpdateChatButtonColor.bind(this);
    this.handleUpdateChatButtonSize = this.handleUpdateChatButtonSize.bind(this);
  }

  /**
   * @param {String} iconType
   */
  handleUpdateChatButtonIcon(iconType) {
    const { client } = this.props;

    let newIconPath = client.chatButton.get("iconPath");

    // If the iconPath was cleared, then the user switches to DEFAULT, we should
    // reset the custom icon path to the default.
    if (iconType === "default" && !client.chatButton.get("iconPath").length) {
      newIconPath = DEFAULT_CHAT_BUTTON_ICON;
    }

    this.updateClient({
      chatButton: client.chatButton.merge({
        iconType,
        iconPath: newIconPath
      })
    });
  }

  /**
   * @param {Object} url
   */
  handleUpdateChatButtonIconImage(url) {
    const { client } = this.props;

    this.updateClient({
      chatButton: client.chatButton.set("iconPath", url)
    });
  }

  /**
   * @param {String} backgroundColor
   */
  handleUpdateChatButtonColor(backgroundColor) {
    const { client } = this.props;

    this.updateClient({
      chatButton: client.chatButton.set("backgroundColor", backgroundColor)
    });
  }

  /**
   * @param {Number} size
   */
  handleUpdateChatButtonSize(size) {
    const { client } = this.props;

    this.updateClient({
      chatButton: client.chatButton.set("size", size)
    });
  }

  /**
   * @param {Object} data
   */
  updateClient(data) {
    const { client, updateClient } = this.props;
    updateClient(client.id, data);
  }

  /**
   * @returns {ReactElement}
   */
  renderChatButtonIcon() {
    const { client } = this.props;
    const size = client.chatButton.get("size");
    const iconSource = client.chatButton.get("iconType") === "default"
      ? DEFAULT_CHAT_BUTTON_ICON
      : client.chatButton.get("iconPath");

    let padding;

    switch (size) {
      case (44):
        padding = 8;
        break;
      case 56:
        padding = 10;
        break;
      case 72:
        padding = 14;
        break;
      default:
        padding = 8;
    }

    const icon = !iconSource
      ? (
        <SvgIcon
          fillColor="none"
          icon="IconPlaceholder"
        />
      )
      : (
        <img
          style={{ width: "100%" }}
          src={iconSource}
          alt="Chat Button Preview"
        />
      );

    return (
      <div style={{ padding, width: "100%", height: "100%" }}>
        {icon}
      </div>
    );
  }

  /**
   * @returns {ReactElement} preview
   */
  renderPreview() {
    const { client } = this.props;
    const size = client.chatButton.get("size");

    return (
      <button
        type="button"
        className="ChatButton__preview__chat-button"
        style={{
          width: size,
          height: size,
          borderRadius: size,
          backgroundColor: client.chatButton.get("backgroundColor")
        }}
      >
        {this.renderChatButtonIcon()}
      </button>
    );
  }

  /**
   * @returns {ReactElement}
   */
  render() {
    const { client } = this.props;

    return (
      <div className="ChatButton">
        <div className="ChatButton__settings">
          <div className="ChatButton__settings__section">
            <div className="g-input__label g-input__label--no-description">Initial Button Icon</div>
            <RadioButtons
              name="initialButtonIcon"
              uniqueIdentifier="initialButtonIcon"
              options={options}
              checked={client.chatButton.get("iconType")}
              onClick={this.handleUpdateChatButtonIcon}
            />
            <div className={classnames(
              "ChatButton__settings__section__text-input", {
                "ChatButton__settings__section__text-input--hidden": client.chatButton.get("iconType") === "default"
              }
            )}
            >
              <MediaUploader
                {...this.props}
                id="chat-button-icon-type"
                name="chat-button-icon-type"
                onChange={this.handleUpdateChatButtonIconImage}
                urlPath={Immutable.Map({ image: client.chatButton.get("iconPath") })}
                urlField="image"
                acceptTypes=".svg"
                placeholder="https://static.ada.support/my-picture.svg"
                endpoint="/images/"
                uploadIcon="Upload"
                notValid={client.invalidFields.includes("chatButtonIconPath")}
              />
            </div>
            <div
              className={classnames(
                "ChatButton__settings__section__text-input__text", {
                  "ChatButton__settings__section__text-input__text--hidden": client.chatButton.get("iconType") === "default"
                }
              )}
            >
              Upload SVGs only.
            </div>
          </div>
          <div className="ChatButton__settings__section">
            <label
              htmlFor="chatIconBackgroundColor"
              className="g-input__label g-input__label--no-description"
            >
              Background Color
            </label>
            <div className="ChatButton__settings__input-color">
              <ColorInput
                id="chatIconBackgroundColor"
                value={client.chatButton.get("backgroundColor")}
                handleChange={this.handleUpdateChatButtonColor}
              />
            </div>
          </div>
          <div className="ChatButton__settings__section">
            <div className="g-input__label g-input__label--no-description">Button Size</div>
            <RadioButtons
              name="buttonSize"
              uniqueIdentifier="buttonSize"
              options={buttonSizeOptions}
              checked={client.chatButton.get("size")}
              onClick={this.handleUpdateChatButtonSize}
            />
          </div>
        </div>
        <div className="ChatButton__preview">
          {this.renderPreview()}
        </div>
      </div>
    );
  }
}

ChatButton.propTypes = {
  client: PropTypes.instanceOf(Client).isRequired,
  platformType: PropTypes.string.isRequired,
  platform: ImmutablePropTypes.map.isRequired,
  updatePlatform: PropTypes.func.isRequired,
  updateClient: PropTypes.func.isRequired
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapStateToProps(state) {
  return {
    client: getResource(state, "client")
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object.<Function>}
 */
function mapDispatchToProps(dispatch) {
  return {
    updateClient: (id, data) => dispatch(updateOne("client", id, data))
  };
}

const Connector = connect(mapStateToProps, mapDispatchToProps)(ChatButton);

Connector.propTypes = {
  platformType: PropTypes.string.isRequired,
  platform: ImmutablePropTypes.map.isRequired,
  updatePlatform: PropTypes.func.isRequired
};

export default Connector;
